import {gql, useQuery} from '@apollo/client';
import {Button} from '@telia/styleguide';
import moment from 'moment';
import React, {FC} from 'react';

import changelogsQuery from '../../graphql/query/changelogs.graphql';

import './Changelogs.scss';

import {DOCUMENTATION_PRODUCT_CHANGES} from '../../appRoutes';
import {useLoading} from '../../hooks/useLoading';
import {useModal} from '../../hooks/useModal';
import {useUser} from '../../hooks/useUser';
import {getLog} from '../../log';
import {Changelog} from '../../model';
import {CHANGELOGS_MANAGE} from '../../permissions';
import {splitTextWithLinks} from '../../utils/links';
import AccordionItem from '../common/AccordionItem';
import {AccordionListFc} from '../common/AccordionList';
import {Icon} from '../common/Icon';
import InformationLineFc from '../common/InformationLine';
import Page from '../common/Page';
import PageSubtitle from '../common/PageSubtitle';
import {PageViewCounter} from '../metrics/PageViewCounter';
import {ChangelogForm} from './ChangelogForm';

const log = getLog('Changelogs', 'INFO');

interface ChangelogsQuery {
  changelogs: Changelog[];
}

interface ChangelogsProps {
  displayLimit?: number;
  hideEdit?: boolean;
}

export const Changelogs: FC<ChangelogsProps> = (props) => {
  const {hasPermission} = useUser();
  const {loading, data: {changelogs} = {}, refetch} = useQuery<ChangelogsQuery>(gql(changelogsQuery), {});
  useLoading([{query: 'messageChannels', isLoading: loading}]);
  const {showModal} = useModal();

  const onEditChangelog = (changelog?: Changelog) => {
    showModal({
      title: `${changelog ? 'Edit' : 'Create'}  Changelog`,
      content: <ChangelogForm changelog={changelog} refetch={refetch} />,
    });
  };

  log.debug('Changelogs', props);

  const isEditButtonDisplayed = hasPermission(CHANGELOGS_MANAGE) && props.hideEdit !== true;

  return (
    <Page>
      <PageSubtitle subtitle="Product Changes" />
      <PageViewCounter page="documents-changelogs" />

      {isEditButtonDisplayed && (
        <Button className="marginBottom" onClick={() => onEditChangelog()} text="Create changelog" />
      )}
      {!changelogs || changelogs?.length === 0 ? (
        <InformationLineFc>No changelogs</InformationLineFc>
      ) : (
        <>
          <AccordionListFc multipleOpen={true} initialOpenItemId="0" className={'noMarginLeft'}>
            {changelogs.isEmpty() ? (
              <InformationLineFc>No changelogs</InformationLineFc>
            ) : (
              changelogs.slice(0, props.displayLimit).map((changelog, i) => {
                const formatDate = (date: string) => moment(date).format('YYYY-MM-DD HH:mm');

                const createdDateFormatted = formatDate(changelog.createdDate);
                const modifiedDateFormatted = formatDate(changelog.modifiedDate);
                const isModifiedDateDisplayed =
                  moment(changelog.modifiedDate).diff(moment(changelog.createdDate), 'hours') > 1;
                const descriptionParts = splitTextWithLinks(changelog.description);

                return (
                  <AccordionItem
                    noBorder={true}
                    id={i.toString()}
                    key={i.toString()}
                    header={
                      <span>
                        <span className="changelog__name marginRight heading--level-3">
                          {changelog.name}
                          {isEditButtonDisplayed && (
                            <Icon
                              icon="edit"
                              className="blueText rem1 changelog__edit"
                              onClick={() => onEditChangelog(changelog)}
                            />
                          )}
                        </span>
                        <div className="changelog__date">
                          {createdDateFormatted}
                          {isModifiedDateDisplayed && (
                            <span className="marginLeft">edited {modifiedDateFormatted}</span>
                          )}
                        </div>
                      </span>
                    }
                    content={
                      <div className="changelog__description">
                        {descriptionParts.map((descriptionPart, index) =>
                          descriptionPart.link ? (
                            <a
                              key={index}
                              className="link support__link"
                              href={`${descriptionPart.link}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {descriptionPart.content}
                            </a>
                          ) : (
                            <span key={index}>{descriptionPart.content}</span>
                          )
                        )}
                      </div>
                    }
                  />
                );
              })
            )}
          </AccordionListFc>
        </>
      )}

      {props.displayLimit && (
        <p className="details">
          Full list of product changes are found in the{' '}
          <a className="link support__link" href={DOCUMENTATION_PRODUCT_CHANGES}>
            Documents
          </a>{' '}
          page.
        </p>
      )}
    </Page>
  );
};
